import { Box, Button, Grid, Modal, TextField, Typography } from "@mui/material";
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useBreakpoint } from "../hooks/useBreakpoint";

export const ShareReportModal = ({
  sharedKey,
  showShareReportModal,
  onClose,
}: {
  sharedKey: string;
  showShareReportModal: boolean;
  onClose: () => void;
}) => {
  const { xs, sm } = useBreakpoint();
  const isMobile = xs || sm;
  const handleCopy = async (includeInstructions: boolean) => {
    const sharedReportCode = "Shared Report Code:";
    const instructions = `To use this code, please do the following:
    
          • Visit https://banks.data.fdic.gov/bankfind-suite/financialreporting
          • Select the kind of report you wish to run
          • Search for and select institutions or peer groups as well as the reporting period as the subject for your report
          • On the reporting interface, change the "Report Type" selector to "Load a Shared Report Code"
          • Copy and paste the “Share Report Code” into the designated area, then press “Generate Report” to return results. Codes can be very long. Be sure to copy the entire block of text.
          `;

    const finalText = includeInstructions
      ? `${instructions}\n${sharedReportCode}\n\n${sharedKey}`
      : sharedKey;

    try {
      await navigator.clipboard.writeText(finalText);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const shareReportModalStyle = {
    position: "absolute",
    top: isMobile ? "100%" : "5%",
    left: "50%",
    transform: isMobile ? "translate(-50%, -100%)" : "translateX(-50%)",
    width: isMobile ? "100%" : 800,
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    pt: 4,
    px: isMobile ? 2 : 4,
    pb: 6,
    textAlign: "center",
    borderRadius: isMobile ? "16px 16px 0 0" : 2,
    outline: "none",
  };

  const buttonStyles = {
    height: 55,
    width: {
      xs: "auto",
      sm: 180,
    },
    minWidth: {
      xs: 0,
      sm: 180,
    },
    px: 2,
    whiteSpace: "nowrap",
    fontSize: {
      xs: "11px",
      sm: "12px",
      md: "14px",
    },
    textAlign: "center",
  };

  return (
    <Modal
      open={showShareReportModal}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={shareReportModalStyle}>
        <Typography
          id="modal-title"
          color="primary.main"
          variant={isMobile ? "h5" : "h2"}
          sx={{ fontWeight: "bold" }}
        >
          Share This Report
        </Typography>

        <Typography
          id="modal-description"
          sx={{
            mt: 2,
            textAlign: "left",
          }}
          color="primary.main"
          variant="body2"
        >
          This interface allows you to share the structure (selected variables)
          of your customized report with others. You are not sharing the
          resulting data of the report, rather the list of variables you
          selected. This will allow others to generate their own reports using
          your report structure for their own report subjects (selected
          institution/peer groups and reporting period.) When comparing reports
          run separately, always ensure that report structure, institutions/peer
          groups, and reporting periods match for identical results.
        </Typography>
        <Typography
          sx={{
            textAlign: "left",
          }}
          color="primary.main"
          variant="body2"
        >
          If you wanted to share the data results of this report, go back and
          select a download results format (raw or formatted) from Download
          Results to send.
        </Typography>
        <Typography
          display="block"
          sx={{
            textAlign: "left",
            fontWeight: "bold",
            paddingBottom: 0,
          }}
          color="primary.main"
          variant="body2"
        >
          How to Use
        </Typography>

        <Typography
          display="block"
          sx={{
            textAlign: "left",
          }}
          color="primary.main"
          variant="body2"
        >
          Copy the text code displayed below to send to the colleagues of your
          choice. We have also provided optional instructions to send with the
          code to help your colleagues easily utilize the shared report code.
        </Typography>
        <Typography
          display="block"
          sx={{
            textAlign: "left",
            fontWeight: "bold",
            paddingBottom: 0,
          }}
          color="primary.main"
          variant="body2"
        >
          Shared Report Code
        </Typography>
        <Grid container spacing={2}>
          <Grid size={12} container spacing={2}>
            <Grid size={9} sx={{ display: "flex" }}>
              <TextField
                fullWidth
                id="share-report-code"
                sx={{
                  input: { color: "rgb(217, 0, 27)", textAlign: "center" },
                }}
                value={sharedKey}
              />
            </Grid>

            <Grid size={3} sx={{ display: "flex" }}>
              <Button
                id="copyCode"
                onClick={() => handleCopy(false)}
                variant="contained"
                color="secondary"
                sx={buttonStyles}
              >
                Copy Code Only
              </Button>
            </Grid>
          </Grid>
          <Grid size={12} container spacing={2} alignItems="stretch">
            <Grid
              size={9}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ pl: 1 }}>
                <Typography
                  sx={{
                    textAlign: "left",
                    fontWeight: "bold",
                    mb: 0.5,
                    paddingBottom: 0,
                  }}
                  color="primary.main"
                  variant="body2"
                >
                  To use this code, please do the following:
                </Typography>
                {[
                  <>
                    Visit{" "}
                    <RouterLink to="/financialreporting">
                      BankFind Suite &gt; Reports and Comparisons
                    </RouterLink>
                  </>,
                  "Select the kind of report you wish to run",
                  "Search for and select institutions or peer groups as well as the reporting period as the subject for your report",
                  "On the reporting interface, change the “Report Type” selector to “Load a Shared Report Code”",
                  "Copy and paste the “Share Report Code” into the designated area, then press “Generate Report” to return results. Codes can be very long. Be sure to copy the entire block of text.",
                ].map((text, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                    }}
                  >
                    <Typography
                      color="primary.main"
                      sx={{
                        mr: 1,
                        ml: 3,
                        fontWeight: "bold",
                      }}
                    >
                      •
                    </Typography>

                    <Typography
                      color="primary.main"
                      variant="body2"
                      sx={{
                        textAlign: "left",
                        m: 0,
                        paddingBottom: 0,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid size={3} sx={{ display: "flex" }}>
              <Button
                id="copyCodeDirections"
                onClick={() => handleCopy(true)}
                variant="contained"
                color="secondary"
                sx={buttonStyles}
              >
                Copy Code & Directions
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid sx={{ mt: 4 }}>
          <Button
            id="close"
            variant="contained"
            sx={{ height: "40px", width: "160px" }}
            onClick={onClose}
          >
            Close
          </Button>
        </Grid>
      </Box>
    </Modal>
  );
};
